import type { CampaignInfo } from '../models/ReportPayload';

/**
 * Detects phishing campaign markers in email content.
 * Ports the campaign detection logic from Ribbon.cs in the VSTO plugin.
 *
 * The VSTO plugin looks for text between "BeginCampaignUzer" and "EndCampaignUzer"
 * markers in the email body to identify training phishing campaigns.
 */
export class CampaignDetector {
  /** Start marker for campaign ID */
  private static readonly BEGIN_MARKER = 'BeginCampaignUzer';

  /** End marker for campaign ID */
  private static readonly END_MARKER = 'EndCampaignUzer';

  /** GUID regex pattern */
  private static readonly GUID_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  /**
   * Detect campaign information from email body.
   * Searches for content between BeginCampaignUzer and EndCampaignUzer markers.
   *
   * @param body - Email body text (plain text or HTML)
   * @returns Campaign detection result
   */
  static detect(body: string): CampaignInfo {
    const result: CampaignInfo = {
      isCampaign: false,
      rawId: '',
      parsedId: null,
    };

    if (!body) {
      return result;
    }

    // Case-insensitive search — matches VSTO OrdinalIgnoreCase behavior
    const bodyUpper = body.toUpperCase();
    const beginIndex = bodyUpper.indexOf(this.BEGIN_MARKER.toUpperCase());
    const endIndex = bodyUpper.indexOf(this.END_MARKER.toUpperCase());

    if (beginIndex === -1 || endIndex === -1 || endIndex <= beginIndex) {
      return result;
    }

    // Extract the raw campaign ID between markers
    const startPos = beginIndex + this.BEGIN_MARKER.length;
    const rawId = body.substring(startPos, endIndex).trim();

    if (!rawId) {
      return result;
    }

    result.isCampaign = true;
    result.rawId = rawId;

    // Try to parse as GUID
    if (this.GUID_PATTERN.test(rawId)) {
      result.parsedId = rawId.toLowerCase();
    } else {
      // Try to extract GUID from the raw string (might have extra characters)
      const guidMatch = rawId.match(
        /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
      );
      if (guidMatch) {
        result.parsedId = guidMatch[0].toLowerCase();
      }
    }

    return result;
  }

  /**
   * Detect campaign from HTML body.
   * First tries raw HTML (markers may be in hidden elements), then strips and retries.
   */
  static detectFromHtml(htmlBody: string): CampaignInfo {
    let result = this.detect(htmlBody);
    if (result.isCampaign) return result;

    const textContent = this.stripHtml(htmlBody);
    return this.detect(textContent);
  }

  /**
   * Detect campaign from raw EML bytes (unmodified MIME content).
   * Outlook Web sanitizes HTML returned by getAsync — this uses the raw EML
   * to find markers that may have been stripped, matching VSTO's mail.HTMLBody access.
   */
  static detectFromEml(emlBuffer: ArrayBuffer): CampaignInfo {
    try {
      const text = new TextDecoder('utf-8', { fatal: false }).decode(emlBuffer);
      return this.detect(text);
    } catch {
      return { isCampaign: false, rawId: '', parsedId: null };
    }
  }

  /**
   * Strip HTML tags from content.
   * Uses DOMParser for safe HTML parsing.
   */
  private static stripHtml(html: string): string {
    if (typeof DOMParser !== 'undefined') {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      return doc.body.textContent || doc.body.innerText || '';
    }

    // Fallback: simple regex-based stripping
    return html
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Check if the email appears to be a training phishing campaign.
   * Convenience method that returns just the boolean.
   *
   * @param body - Email body text
   * @returns True if campaign markers are found
   */
  static isCampaignEmail(body: string): boolean {
    return this.detect(body).isCampaign;
  }

  /**
   * Get campaign ID for use in report URL.
   * Returns parsed GUID if available, otherwise raw ID, or empty string.
   *
   * @param body - Email body text
   * @returns Campaign ID string for URL
   */
  static getCampaignIdForUrl(body: string): string {
    const info = this.detect(body);
    return info.parsedId || info.rawId || '';
  }
}
